import { Waterfall, WaterfallContract, WaterfallRightholder, isProducer } from './waterfall';
import { Right, getRightConditions } from './right';
import { Income } from '../income';
import { Amortization } from './amortization';
import { getContractsWith } from '../contract';
import { RightholderRole, StatementType, rightholderGroups, statementsRolesMapping } from '../static';
import { ConditionName, isConditionWithTarget, OrgRevenuCondition, skipTakeFromUntilConditions } from './conditions';
import { defaultColor } from './graph';

/**
 * @see https://www.notion.so/Waterfall-Colors-Mini-Documentation-1c989f573eb1803fbe2af1694389b5ab
 */
const rightholderColors = [
  '#66DF9C', // Producer color
  '#FFE500',
  '#3A90F3',
  '#FF46CB',
  '#FF0303',
  '#5F0A87',
  '#51D6FF',
  '#351E29',
  '#A3E635',
  '#F97316',
  '#14b8a6',
  '#9333ea',
  '#ef4444',
  '#22c55e',
  '#eab308',
  '#3b82f6',
  '#ec4899',
  '#6B7280',
  '#be185d',
  '#059669',
  '#19e555',
  '#9019e5',
  '#e5cc19',
  '#19c3e5',
  '#e51987',
  '#4ce519',
  '#2219e5',
  '#e55d19',
  '#19e599',
  '#d419e5',
  '#bae519',
  '#197fe5',
  '#e51943',
  '#19e52a',
  '#6619e5',
  '#e5a119',
  '#19e5dd',
  '#e519b2',
  '#76e519',
  '#193ae5',
  '#e53319',
  '#19e56f',
  '#aa19e5',
  '#e4e519',
  '#19a9e5',
  '#e5196d',
  '#32e519',
  '#3c19e5',
  '#e57719',
  '#19e5b3',
  '#e519dc',
  '#a0e519',
  '#1965e5',
  '#e51929',
  '#19e544',
  '#8019e5',
  '#e5bc19',
  '#19d3e5',
  '#e51997',
  '#5ce519',
  '#1920e5',
  '#e54d19',
  '#19e589',
  '#c419e5',
  '#cae519',
  '#198fe5',
  '#e51953',
  '#19e51a',
  '#5619e5',
  '#e59119',
];

export const producerColor = rightholderColors[0];

function getRandomColor() {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export function needAColor(rightholder: WaterfallRightholder) {
  return !rightholder.color || rightholder.color === defaultColor;
}

export function applyRightholderColors(rightholders: WaterfallRightholder[]) {
  const getUnusedColor = () => {
    const color = rightholderColors.find(c => !rightholders.some(r => r.color === c));
    return color || getRandomColor();
  };

  for (const rightholder of rightholders.filter(needAColor)) {
    if (isProducer(rightholder)) {
      rightholder.color = producerColor;
    } else {
      rightholder.color = getUnusedColor();
    }
  }
}

export function canCreateStatement(type: StatementType, rightholder: WaterfallRightholder, producer: WaterfallRightholder, contracts: WaterfallContract[], canBypassRules = false): boolean {
  if (!type) return false;
  if (canBypassRules) return true;
  // Only the producer can create direct sales and producer statements, and producer is always an admin
  if (['directSales', 'producer'].includes(type)) return false;
  if (!rightholder.roles.some(role => statementsRolesMapping[type].includes(role))) return false;
  return getContractsWith([producer.id, rightholder.id], contracts).filter(c => statementsRolesMapping[type].includes(c.type)).length !== 0;
}

export function canOnlyReadStatements(rightholder: WaterfallRightholder, canBypassRules = false) {
  return !canBypassRules && !rightholder?.roles.some(r => rightholderGroups.withStatements.includes(r));
}

export function canDeleteContract(contractId: string, _rights: Right[], incomes: Income[], amortizations: Amortization[], waterfall: Waterfall) {
  const rights = _rights.filter(r => r.contractId === contractId);
  const conditions = _rights.flatMap(r => getRightConditions(r));
  const expenseTypeIds = waterfall.expenseTypes?.[contractId]?.map(e => e.id);
  const contractIdsInConditions = skipTakeFromUntilConditions(conditions).map(c => {
    if (isConditionWithTarget(c) && (typeof c.payload.target === 'object' && c.payload.target.in === 'contracts.investment')) {
      return c.payload.target.id;
    } else if (isConditionWithTarget(c) && expenseTypeIds?.length && (typeof c.payload.target === 'object' && c.payload.target.in === 'expense' && expenseTypeIds.includes(c.payload.target.id))) {
      return contractId;
    } else if (c.name === 'interest') {
      return c.payload.contractId;
    }
  }).filter(id => !!id);

  const contractIdsInIncomes = incomes.filter(i => i.status === 'received' && i.contractId === contractId).map(i => i.contractId);
  const isInIncomes = contractIdsInIncomes.includes(contractId);
  const isInConditions = contractIdsInConditions.includes(contractId);
  const isInRights = rights.length !== 0;
  const isInAmortizations = amortizations.filter(a => a.contractIds.includes(contractId)).length !== 0;

  return {
    canRemoveContract: !isInRights && !isInConditions && !isInIncomes && !isInAmortizations,
    isInRights,
    isInConditions,
    isInIncomes,
    isInAmortizations,
    rights
  };
}

export function canDeleteRightholder(rightholderId: string, rights: Right[]) {
  const _conditions = rights.flatMap(r => getRightConditions(r));
  const conditions = skipTakeFromUntilConditions(_conditions);
  const conditionsToCheck: ConditionName[] = ['orgRevenu', 'orgTurnover'];
  const isRightholderInConditions = conditions.filter(c => conditionsToCheck.includes(c.name) && (c.payload as OrgRevenuCondition).orgId === rightholderId).length !== 0;
  const hasLinkedRights = rights.filter(r => r.rightholderId === rightholderId).length > 0;
  return { isRightholderInConditions, hasLinkedRights };
}

export const dealTypes: RightholderRole[] = ['sale', 'localDistributor'];

export const dsmTypes: RightholderRole[] = ['author', 'other', 'performer'];
